// The boat's own question, on every page of that boat.
//
// The anchor assistant asks two things and ACTS when nobody answers: an
// arrival prompt arms the watch on its deadline (fail closed), a departure
// prompt leaves it set. Until this component existed the question was
// published to `computed.anchor.prompt` and rendered nowhere, so in practice
// every prompt was answered by the boat's own timeout. That is why this lives
// in the chrome rather than on the anchor module: the owner is as likely to be
// on Alarms or Devices when the boat asks, and a question with a clock on it
// must not depend on which tab happens to be open.
//
// It renders NOTHING at all unless the boat is currently asking. That is what
// lets it sit above the route content on the height-locked dashboard route
// without costing anyone a pixel the rest of the time.
//
// NON-OPTIMISTIC, and this is the whole design (see `lib/anchorPrompt.ts`):
//
//   - the countdown keeps running after an answer is sent;
//   - the buttons stay on screen;
//   - the confirmation is the BOAT clearing `computed.anchor.prompt`, which
//     unmounts this banner — never the cloud's 202, which only means the frame
//     was taken for relay;
//   - about five seconds after that 202 with no flip, it says the answer may
//     not have arrived, rather than looking like it worked.
//
// Every word and every arithmetic decision comes from `lib/anchorPrompt.ts`,
// which is tested without a DOM. This file posts and renders.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../../api';
import {
  ANCHOR_PROMPT_PATH,
  PROMPT_EXPIRED_COPY,
  REMEMBER_LABEL,
  clockSkewMs,
  describeAnswerError,
  promptCopy,
  promptCountdown,
  promptStatusLine,
  readAnchorPrompt,
  responseStillApplies,
  type AnchorPromptResponse,
  type PromptResponseState,
} from '../../lib/anchorPrompt';
import { useTelemetryLatest } from '../../lib/useTelemetry';
import type { LiveSample } from '../../lib/useBoatLiveData';

/**
 * `?prompt=anchor` — where a push notification about the question lands. It
 * scrolls the banner into view and marks it for a few seconds, then clears
 * itself out of the URL so a re-render or a back-button does not re-highlight
 * a banner the owner has already read.
 */
const PROMPT_DEEP_LINK_PARAM = 'prompt';
const PROMPT_DEEP_LINK_VALUE = 'anchor';
const HIGHLIGHT_MS = 10_000;

/** Scoped to the one path: this banner has no business reading the whole boat. */
const SEED_PATHS = [ANCHOR_PROMPT_PATH];

export interface AnchorPromptBannerProps {
  boatId: string;
  /** The boat-level telemetry snapshot; the question rides one path in it. */
  liveSamples: ReadonlyMap<string, LiveSample> | undefined;
  /** The chrome's 1 Hz clock. One timer for the shell, not one per banner. */
  nowMs: number;
}

export function AnchorPromptBanner({ boatId, liveSamples, nowMs }: AnchorPromptBannerProps) {
  // TWO SOURCES, live first — the same precedence the alarm surfaces use.
  //
  // The live lane only holds what has come over the socket SINCE this tab
  // opened, and the boat writes this path once when it raises the question. A
  // tab opened after that would see nothing at all, which is precisely the tab
  // the owner reaches for when a notification wakes them. So the stored latest
  // seeds first paint.
  //
  // Live must WIN, though: settling the prompt republishes the path as null,
  // and that null is the only thing that takes this banner off the screen. A
  // seed that outranked it would leave a settled question on every page until
  // the tab was reloaded.
  const seed = useTelemetryLatest(boatId, SEED_PATHS).get(ANCHOR_PROMPT_PATH);
  const liveSample = liveSamples?.get(ANCHOR_PROMPT_PATH);
  // The stored row carries no arrival instant, and inventing one would be
  // worse than none: `receivedAt - time` on a row that has been sitting in the
  // store for ten minutes measures its AGE, not the boat's clock error, and
  // correcting by it would inflate the countdown by exactly that age — in the
  // dangerous direction. `clockSkewMs` reads a missing `receivedAt` as no
  // correction, which is the honest answer here.
  const sample: { time?: string; value?: unknown; receivedAt?: number } | undefined =
    liveSample ?? seed;
  const prompt = readAnchorPrompt(sample?.value);
  const promptId = prompt?.promptId ?? null;

  const [sent, setSent] = useState<PromptResponseState | null>(null);
  const [remember, setRemember] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const bannerRef = useRef<HTMLElement | null>(null);

  // A new question is a new decision: the previous answer's wait, its warning
  // and its remember tick must not carry over onto it.
  useEffect(() => {
    setSent((prev) => (prev !== null && prev.promptId === promptId ? prev : null));
    setRemember(false);
  }, [promptId]);

  // The deep link is consumed only once there is a question to point at — a
  // notification can easily beat the telemetry seed, and clearing the param on
  // an empty banner would throw the highlight away.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinked = searchParams.get(PROMPT_DEEP_LINK_PARAM) === PROMPT_DEEP_LINK_VALUE;
  useEffect(() => {
    if (!deepLinked || promptId === null) return;
    setHighlighted(true);
    bannerRef.current?.scrollIntoView({ block: 'nearest' });
    setSearchParams(
      (prev) => {
        // Merge, never replace: other params on this URL belong to someone else.
        const next = new URLSearchParams(prev);
        next.delete(PROMPT_DEEP_LINK_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [deepLinked, promptId, setSearchParams]);

  // The mark fades on its own clock. It cannot be a timeout owned by the effect
  // above: clearing the param flips `deepLinked` in the very next commit, which
  // tears that effect down — and takes its timeout with it, leaving the banner
  // marked forever.
  useEffect(() => {
    if (!highlighted) return;
    const id = window.setTimeout(() => setHighlighted(false), HIGHLIGHT_MS);
    return () => window.clearTimeout(id);
  }, [highlighted]);

  const answer = useCallback(
    async (response: AnchorPromptResponse, promptIdToAnswer: string, rememberIt: boolean) => {
      setSent({ promptId: promptIdToAnswer, response, acceptedAtMs: null, error: null });
      try {
        await api<{ queued: boolean }>(`/v1/boats/${boatId}/anchor-prompt-response`, {
          method: 'POST',
          // The cloud returns no command key for a prompt answer, on purpose:
          // the boat registers no status echo for it. The prompt disappearing
          // from telemetry is the only receipt there is.
          body: {
            promptId: promptIdToAnswer,
            response,
            ...(rememberIt ? { remember: true } : {}),
          },
          idempotencyKey: crypto.randomUUID(),
        });
        setSent((prev) =>
          prev !== null && prev.promptId === promptIdToAnswer
            ? { ...prev, acceptedAtMs: Date.now() }
            : prev
        );
      } catch (err) {
        const message = describeAnswerError(err);
        setSent((prev) =>
          prev !== null && prev.promptId === promptIdToAnswer ? { ...prev, error: message } : prev
        );
      }
    },
    [boatId]
  );

  if (prompt === null) return null;

  const countdown = promptCountdown({
    prompt,
    skewMs: clockSkewMs(sample ?? null),
    nowMs,
  });
  // Past the deadline AND past the grace the boat has settled this one way or
  // another and we are looking at a leftover in the sample map. A dead clock
  // over live buttons is worse than no banner.
  if (countdown.stale) return null;

  const copy = promptCopy(prompt.kind);
  const status = promptStatusLine({
    sent: responseStillApplies(sent, prompt) ? sent : null,
    livePrompt: prompt,
    nowMs,
  });

  return (
    <section
      ref={bannerRef}
      className={`anchor-prompt${highlighted ? ' anchor-prompt--highlight' : ''}`}
      aria-label="The boat has a question about the anchor watch"
      data-testid={`anchor-prompt-${boatId}`}
    >
      <div className="anchor-prompt__text">
        <span className="anchor-prompt__question" data-testid="anchor-prompt-question">
          {copy.question}
        </span>
        <span className="anchor-prompt__consequence">{copy.ifIgnored}</span>
        {status !== null ? (
          <span
            className="anchor-prompt__status"
            data-tone={status.tone}
            role="status"
            data-testid="anchor-prompt-status"
          >
            {status.text}
          </span>
        ) : null}
      </div>

      <span className="anchor-prompt__clock" data-testid="anchor-prompt-countdown">
        {countdown.expired ? PROMPT_EXPIRED_COPY : `${countdown.text} left`}
      </span>

      <div className="anchor-prompt__actions">
        {/* Offered on the arrival question only: "always" is a standing answer
            to "shall I arm?", and there is no standing answer to "are you
            leaving?" — that one is about this moment. */}
        {prompt.kind === 'first_arm' ? (
          <label className="anchor-prompt__remember">
            <input
              type="checkbox"
              checked={remember}
              data-testid="anchor-prompt-remember"
              onChange={(e) => setRemember(e.target.checked)}
            />
            {REMEMBER_LABEL}
          </label>
        ) : null}
        {copy.actions.map((action) => (
          <button
            key={action.response}
            type="button"
            className={`anchor-prompt__btn${action.primary ? ' anchor-prompt__btn--primary' : ''}`}
            data-testid={`anchor-prompt-${action.response}`}
            onClick={() => void answer(action.response, prompt.promptId, remember)}
          >
            {action.label}
          </button>
        ))}
      </div>
      {/* No dismiss. The boat is going to act on the deadline either way, so
          hiding the question would only take away the warning. */}
    </section>
  );
}
